import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Icon from '../common/containers/icon';
import entityComposition from '../common/classes/entityComposition';
import appHistory from '../../utils/appHistory';
import FavoritesModal from '../favorites/favorites';
import { getAllFavorites, filterFavorites } from '../favorites/favoritesHelpers';
import { useUserPreferencesFavorites } from '../favorites/userPreferencesFavorites';
import { getSidebarNavEntries, shouldMountAdminMenu } from './navItems';
import AdminMenu from './adminMenu';
import TabGroupSidebarMenu from './tabGroupSidebarMenu';
import SidebarDirectFormNavItem from './sidebarDirectFormNavItem';
import useLandingRedirect from './useLandingRedirect';

/**
 * Vertical icon navigation for the sidebar layout.
 *
 * Receives expand state from SidebarLayout. Connects to Redux for nav item data.
 * Calls useHeaderInitialization to load settings when sidebar layout is active
 * (Header is not rendered in sidebar layout, so we must load settings here).
 */
const GlobalNavSidebar = ({
  user: _user = null,
  cabinets: _cabinets = [],
  dashboards: _dashboards = [],
  tabGroups: _tabGroups = [],
  settingsLoaded: _settingsLoaded = false,
  favoritesEnabled: _favoritesEnabled = false,
  userContent: _userContent = null,
  userActions: _userActions = {},
  configBehaviorActions,
  settingsActions,
  expanded,
  drawerOpen,
  mobileNavHidden: _mobileNavHidden = false,
  onToggleExpanded,
  onCloseDrawer,
  location
}) => {
  const [favoritesModalOpen, setFavoritesModalOpen] = useState(false);
  const [favoritesQuery, setFavoritesQuery] = useState('');
  const {
    preferencesContent,
    ensureLoaded: ensurePreferencesFavoritesLoaded
  } = useUserPreferencesFavorites();
  useEffect(() => {
    if (_settingsLoaded) return;
    settingsActions.serverLoad();
    configBehaviorActions.serverIndex({});
  }, [_settingsLoaded, configBehaviorActions, settingsActions]);
  useLandingRedirect({
    user: _user,
    cabinets: _cabinets,
    dashboards: _dashboards,
    settingsLoaded: _settingsLoaded
  });
  const userId = _user === null || _user === void 0 ? void 0 : _user.user_id;
  const loadUserSavedViews = _userActions.serverShow;
  useEffect(() => {
    if (!_favoritesEnabled || !userId || !_settingsLoaded || !favoritesModalOpen) {
      return;
    }
    ensurePreferencesFavoritesLoaded({
      force: true
    }).catch(() => {});
    if (loadUserSavedViews && _userContent == null) {
      loadUserSavedViews(userId).catch(() => {});
    }
  }, [_favoritesEnabled, userId, _settingsLoaded, favoritesModalOpen, ensurePreferencesFavoritesLoaded, loadUserSavedViews, _userContent]);
  const filteredFavorites = useMemo(() => {
    if (!_user) return [];
    const allFavorites = getAllFavorites(_userContent, _cabinets || [], preferencesContent);
    return filterFavorites(allFavorites, favoritesQuery);
  }, [_user, _userContent, _cabinets, favoritesQuery, preferencesContent]);
  const handleOpenFavorites = () => {
    setFavoritesModalOpen(true);
    setFavoritesQuery('');
  };
  const handleCloseFavorites = () => {
    setFavoritesModalOpen(false);
    setFavoritesQuery('');
  };
  const {
    pathname
  } = location;
  const showLabels = expanded || drawerOpen;
  const collapsedTitle = text => showLabels ? undefined : text;
  const navEntries = _user ? getSidebarNavEntries({
    user: _user,
    cabinets: _cabinets,
    dashboards: _dashboards,
    tabGroups: _tabGroups
  }) : [];
  const isActive = href => {
    const path = href.replace('/#', '');
    return pathname.startsWith(path);
  };
  const handleLogout = () => {
    appHistory.push('/logout');
  };
  const sidebarClass = ['global-nav-sidebar', expanded ? 'global-nav-sidebar--expanded' : '', drawerOpen ? 'global-nav-sidebar--drawer-open' : ''].filter(Boolean).join(' ');
  const renderNavControl = ({
    href,
    onClick,
    icon,
    label,
    active: _active = false,
    className: _className = '',
    tag: Tag = 'a'
  }) => /*#__PURE__*/React.createElement(Tag, {
    href: Tag === 'a' ? href : undefined,
    type: Tag === 'button' ? 'button' : undefined,
    className: `global-nav-sidebar__link${_active ? ' global-nav-sidebar__link--active' : ''}${_className ? ` ${_className}` : ''}`,
    "aria-current": _active ? 'page' : undefined,
    "aria-label": !showLabels ? label : undefined,
    title: collapsedTitle(label),
    onClick: onClick,
    tabIndex: _mobileNavHidden ? -1 : undefined,
    disabled: Tag === 'button' && _mobileNavHidden ? true : undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: "global-nav-sidebar__icon"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: icon
  })), /*#__PURE__*/React.createElement("span", {
    className: "global-nav-sidebar__label"
  }, label));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("nav", {
    className: sidebarClass,
    "aria-label": "Main navigation",
    id: "global-nav-sidebar",
    "aria-hidden": _mobileNavHidden || undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: "global-nav-sidebar__toggle-wrap"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "global-nav-sidebar__link global-nav-sidebar__toggle",
    onClick: onToggleExpanded,
    "aria-expanded": expanded,
    "aria-controls": "global-nav-sidebar",
    "aria-label": expanded ? 'Collapse sidebar' : 'Expand sidebar',
    title: expanded ? 'Icon only' : 'Show labels',
    tabIndex: _mobileNavHidden ? -1 : undefined,
    disabled: _mobileNavHidden || undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: "global-nav-sidebar__icon"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: expanded ? 'angle-double-left' : 'angle-double-right'
  })))), /*#__PURE__*/React.createElement("ul", {
    className: "global-nav-sidebar__nav"
  }, navEntries.map(entry => {
    if (entry.type === 'dropdown') {
      return /*#__PURE__*/React.createElement(TabGroupSidebarMenu, {
        key: entry.key,
        label: entry.label,
        icon: entry.icon,
        className: entry.className,
        items: entry.items,
        expanded: expanded,
        drawerOpen: drawerOpen,
        mobileNavHidden: _mobileNavHidden,
        onCloseDrawer: onCloseDrawer,
        pathname: pathname
      });
    }
    if (entry.type === 'directForm') {
      return /*#__PURE__*/React.createElement(SidebarDirectFormNavItem, {
        key: entry.key,
        identifier: entry.identifier,
        title: entry.label,
        icon: entry.icon,
        directFormType: entry.directFormType,
        className: entry.className,
        renderControl: renderNavControl,
        onCloseDrawer: onCloseDrawer
      });
    }
    const {
      key,
      className,
      href,
      icon,
      label
    } = entry;
    const active = isActive(href);
    return /*#__PURE__*/React.createElement("li", {
      key: key,
      className: `global-nav-sidebar__item ${className}`
    }, renderNavControl({
      href,
      icon,
      label,
      active,
      onClick: onCloseDrawer
    }));
  })), /*#__PURE__*/React.createElement("ul", {
    className: "global-nav-sidebar__bottom"
  }, _favoritesEnabled && /*#__PURE__*/React.createElement("li", {
    className: "global-nav-sidebar__item t-h-nav-favorites"
  }, renderNavControl({
    tag: 'button',
    icon: 'star',
    label: 'Favorites',
    onClick: () => {
      onCloseDrawer();
      handleOpenFavorites();
    }
  })), shouldMountAdminMenu(_cabinets) && /*#__PURE__*/React.createElement(AdminMenu, {
    variant: "sidebar",
    expanded: expanded,
    drawerOpen: drawerOpen,
    mobileNavHidden: _mobileNavHidden,
    onCloseDrawer: onCloseDrawer
  }), /*#__PURE__*/React.createElement("li", {
    className: "global-nav-sidebar__item t-h-nav-logout"
  }, renderNavControl({
    tag: 'button',
    icon: 'sign-out-alt',
    label: 'Log Out',
    onClick: handleLogout
  })))), _favoritesEnabled && /*#__PURE__*/React.createElement(FavoritesModal, {
    isOpen: favoritesModalOpen,
    onClose: handleCloseFavorites,
    query: favoritesQuery,
    onQueryChange: setFavoritesQuery,
    favorites: filteredFavorites
  }));
};
GlobalNavSidebar.propTypes = {
  user: PropTypes.shape({
    user_id: PropTypes.string,
    permissions: PropTypes.shape({
      general: PropTypes.arrayOf(PropTypes.string)
    })
  }),
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    denyPermissions: PropTypes.arrayOf(PropTypes.string),
    allowPermissions: PropTypes.arrayOf(PropTypes.string),
    client: PropTypes.shape({
      web: PropTypes.shape({
        layout: PropTypes.shape({
          superset: PropTypes.string
        }),
        icon: PropTypes.string,
        tabGroup: PropTypes.shape({
          identifier: PropTypes.string
        })
      })
    })
  })),
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string
  })),
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })),
  settingsLoaded: PropTypes.bool,
  favoritesEnabled: PropTypes.bool,
  userContent: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    savedViews: PropTypes.shape({})
  }),
  userActions: PropTypes.shape({
    change: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func
  }),
  configBehaviorActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  settingsActions: PropTypes.shape({
    serverLoad: PropTypes.func
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  mobileNavHidden: PropTypes.bool,
  onToggleExpanded: PropTypes.func.isRequired,
  onCloseDrawer: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired
};
export { GlobalNavSidebar as TestGlobalNavSidebar };
export default withRouter(entityComposition(GlobalNavSidebar, ['settings', 'user'], {
  user: state => state.login.user,
  cabinets: state => state.settings.settings.cabinetDefinitions,
  dashboards: state => {
    var _state$login;
    return (state === null || state === void 0 || (_state$login = state.login) === null || _state$login === void 0 || (_state$login = _state$login.user) === null || _state$login === void 0 || (_state$login = _state$login.superset) === null || _state$login === void 0 ? void 0 : _state$login.dashboards) || [];
  },
  tabGroups: state => state.settings.settings.tabGroupDefinitions || [],
  settingsLoaded: state => state.settings.settings && Object.keys(state.settings.settings).length > 0,
  favoritesEnabled: state => {
    var _state$settings;
    return ((_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 ? void 0 : _state$settings.favoritesEnabled) || false;
  },
  userContent: state => {
    var _state$user;
    return (_state$user = state.user) === null || _state$user === void 0 || (_state$user = _state$user.active) === null || _state$user === void 0 ? void 0 : _state$user.content;
  }
}, ['configBehavior']));